import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ArticleLayout from '@/components/ArticleLayout';

export default function SpreadsheetPage() {
  const { t } = useTranslation('home');

  const categories = [
    { label: 'cat_shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
    { label: 'cat_hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-shirt-line' },
    { label: 'cat_tshirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', icon: 'ri-t-shirt-line' },
    { label: 'cat_jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-windy-line' },
    { label: 'cat_pants', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', icon: 'ri-scissors-cut-line' },
    { label: 'cat_headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', icon: 'ri-user-smile-line' },
    { label: 'cat_sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', icon: 'ri-stack-line' },
    { label: 'cat_underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', icon: 'ri-t-shirt-air-line' },
    { label: 'cat_jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', icon: 'ri-shirt-line' },
    { label: 'cat_accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-keyboard-box-line' },
    { label: 'cat_bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-shopping-bag-3-line' },
    { label: 'cat_belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', icon: 'ri-links-line' },
    { label: 'cat_perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', icon: 'ri-drop-line' },
    { label: 'cat_other', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', icon: 'ri-more-line' },
  ];

  return (
    <ArticleLayout
      title="CSSBuy Spreadsheet 2026: Products, Finds & Complete Guide"
      description="The complete CSSBuy Spreadsheet hub with organized product categories, guides, and resources for buyers."
      breadcrumbs={[
        { label: 'Home', to: '/' },
        { label: 'Spreadsheet', to: '/spreadsheet' },
      ]}
      lastUpdated="2026-08-30"
    >
      <h2>What is CSSBuy Spreadsheet?</h2>
      <p>
        CSSBuy Spreadsheet is an organized resource for discovering and comparing products, links, and buying resources related to CSSBuy. It brings together product categories, seller information, QC guides, shipping details, and buying tips in one place for easier research.
      </p>

      <h2>How to Use the Spreadsheet</h2>
      <p>
        The spreadsheet is organized by product categories. Browse the categories below to find the type of products you are looking for. Each category links to curated resources where you can explore products and sellers. Always verify current information before making purchases.
      </p>

      <h2>Product Categories</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {categories.map((cat) => (
          <a
            key={cat.label}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="group flex items-center gap-3 p-4 bg-background-50 border border-background-200 rounded-xl hover:bg-primary-50 hover:border-primary-200 transition-all"
          >
            <div className="w-9 h-9 rounded-lg bg-primary-100 group-hover:bg-primary-200 flex items-center justify-center shrink-0 transition-colors">
              <i className={`${cat.icon} text-primary-600`}></i>
            </div>
            <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700">
              {t(cat.label)}
            </span>
            <i className="ri-arrow-right-up-line ml-auto text-foreground-400 group-hover:text-primary-600 text-sm"></i>
          </a>
        ))}
      </div>

      <h2>How to Search</h2>
      <p>
        Use the category links above to browse products by type. When you find a product link, copy it and submit it to CSSBuy for purchase. Always verify the seller's reputation and check recent reviews before ordering.
      </p>

      <h2>How to Check Sellers</h2>
      <p>
        Before buying from any seller, check their transaction history, reviews, and community feedback. Look for sellers with consistent positive feedback and a history of reliable transactions. Be cautious with new sellers or those with mixed reviews.
      </p>

      <h2>How to Use QC</h2>
      <p>
        Quality Control is essential for verifying your items before international shipping. Always review QC photos carefully before approving your shipment. Check our <Link to="/qc/cssbuy-qc-guide">CSSBuy QC Guide</Link> for detailed instructions on what to look for.
      </p>

      <h2>Shipping Information</h2>
      <p>
        Shipping costs depend on weight, volume, destination, and chosen shipping line. Review our <Link to="/shipping/cssbuy-shipping-cost">Shipping Cost Guide</Link> for detailed information on how shipping costs are calculated and how to reduce them.
      </p>

      <h2>Safety Tips</h2>
      <p>
        Always verify URLs, sellers, and product details before purchasing. Never share your account password. Start with small orders to learn the process. Visit our <Link to="/safety">Safety Center</Link> for comprehensive safety guidelines.
      </p>

      <h2>Frequently Asked Questions</h2>
      <p>
        <strong>Is the CSSBuy Spreadsheet free?</strong> Yes, this is a free informational resource for research purposes.
      </p>
      <p>
        <strong>Do you sell products directly?</strong> No, we do not sell products. We provide information to help you research before buying through CSSBuy.
      </p>
      <p>
        <strong>Are prices accurate?</strong> Prices change frequently. Always verify current prices on the seller's page before ordering.
      </p>

      <h2>Related Guides</h2>
      <ul>
        <li><Link to="/reviews/cssbuy-review">CSSBuy Review 2026</Link></li>
        <li><Link to="/shipping/cssbuy-shipping-cost">CSSBuy Shipping Cost Guide</Link></li>
        <li><Link to="/warehouse/cssbuy-warehouse">CSSBuy Warehouse Guide</Link></li>
        <li><Link to="/qc/cssbuy-qc-guide">CSSBuy QC Guide</Link></li>
        <li><Link to="/safety">CSSBuy Safety Center</Link></li>
      </ul>
    </ArticleLayout>
  );
}